import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './marketing.css';

const FEATURES = [
  { icon: '⚡', title: 'AI Caption Generator', desc: 'Generate scroll-stopping captions for Instagram, TikTok, Twitter, LinkedIn and Facebook in seconds. Trained on millions of viral posts.' },
  { icon: '🔥', title: 'Viral Post Formats', desc: 'Access 50+ proven viral post templates — hooks, storytelling frameworks, controversy posts, and engagement bait formats that actually work.' },
  { icon: '#️⃣', title: 'Smart Hashtag Engine', desc: 'AI finds the perfect hashtags for maximum reach. Analyses your niche, competitor content, and trending topics to get your posts discovered.' },
  { icon: '🎯', title: 'Multi-Platform Content', desc: 'One idea → content for every platform. ViralVolt rewrites and reformats your content to fit Instagram, TikTok, X, LinkedIn, and YouTube Shorts.' },
  { icon: '🧠', title: 'Brand Voice AI', desc: 'Train ViralVolt on your brand tone, style, and audience. Every post sounds like you — not like a robot — every single time.' },
  { icon: '📈', title: 'Trend Intelligence', desc: 'Real-time trend monitoring across every major platform. Get alerted when a trend fits your niche so you can ride the wave before it peaks.' },
];

const PLATFORMS = ['Instagram', 'TikTok', 'Twitter / X', 'LinkedIn', 'Facebook', 'YouTube Shorts'];

const FAQS = [
  { q: 'What platforms does ViralVolt support?', a: 'ViralVolt creates content for Instagram, TikTok, Twitter/X, LinkedIn, Facebook, and YouTube Shorts — all from a single dashboard.' },
  { q: 'How is ViralVolt different from other AI writing tools?', a: 'ViralVolt is built specifically for social media virality. It\'s trained on millions of high-performing posts and understands platform-specific algorithms, not just general writing.' },
  { q: 'Can I train it on my brand voice?', a: 'Yes. You can upload examples of your existing content and ViralVolt\'s Brand Voice AI will match your tone, style, and language in every post it generates.' },
  { q: 'Is ViralVolt part of Gantima?', a: 'Yes. ViralVolt is a product built by Gantima Software. It\'s our AI social media tool — part of our broader suite of AI-powered automation products.' },
];

export default function ViralVolt() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="mkt-root">
      <Helmet>
        <title>ViralVolt | AI Social Media Content Creator — Go Viral on Autopilot</title>
        <meta name="description" content="ViralVolt by Gantima is an AI social media content creator that generates viral captions, hashtags, and posts for Instagram, TikTok, Twitter, LinkedIn and more. Go viral on autopilot." />
        <meta name="keywords" content="AI social media content creator, viral post generator, AI caption generator, hashtag generator, ViralVolt, Gantima ViralVolt, social media AI tool, TikTok caption AI, Instagram AI content" />
        <link rel="canonical" href="https://www.gantima.com/viralvoltai" />
        <meta property="og:title" content="ViralVolt | AI Social Media Content Creator" />
        <meta property="og:description" content="Go viral on autopilot. ViralVolt generates scroll-stopping captions, hashtags and posts for every platform — powered by AI." />
        <meta property="og:url" content="https://www.gantima.com/viralvoltai" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "ViralVolt",
          "applicationCategory": "SocialNetworkingApplication",
          "operatingSystem": "Web",
          "description": "AI social media content creator that generates viral posts, captions, and hashtags for all major platforms.",
          "url": "https://www.gantima.com/viralvoltai",
          "author": { "@type": "Organization", "name": "Gantima Software", "url": "https://www.gantima.com" },
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Nav */}
      <nav className="mkt-nav">
        <a href="/" className="mkt-nav-brand" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <img src="/GANTIMA-Logo.png" alt="Gantima" className="mkt-nav-logo-left-img" />
          <span className="mkt-nav-brand-tag">SOFTWARE AUTOMATION</span>
        </a>
        
        <ul className="mkt-nav-links">
          <li><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a></li>
          <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Services</a></li>
          <li><a href="/pricing" onClick={e => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a></li>
          <li className="mkt-nav-dropdown">
            <span className="mkt-nav-dropdown-trigger">Products ▾</span>
            <div className="mkt-nav-dropdown-menu">
              <a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="mkt-nav-dropdown-featured">
                <div>
                  <div className="mkt-nav-dropdown-name">ViralVolt.ai</div>
                  <div className="mkt-nav-dropdown-desc">AI Social Media Marketing Tool</div>
                </div>
              </a>
              <div className="mkt-nav-dropdown-label">Solutions by Industry</div>
              <div className="mkt-nav-dropdown-grid">
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>E-Commerce</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HealthTech</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>FinTech & Insurance</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Algorithmic Trading</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Crypto & DeFi</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Legal & Compliance</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Real Estate</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HR & Recruiting</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Logistics & Supply Chain</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>SaaS & Tech</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Hedge Funds & Prop Trading</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Forex & Futures</a>
                <a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="viralvolt-link">Social Media Marketing</a>
              </div>
            </div>
          </li>
          <li><a href="/about" onClick={e => { e.preventDefault(); navigate('/about'); }}>About</a></li>
          <li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li>
        </ul>
        <div className="mkt-nav-actions">
          <button className="mkt-btn mkt-btn-primary mkt-btn-glow" onClick={() => navigate('/')}>Book Free Consultation</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '130px 5% 90px', background: '#020817', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        {/* Glows */}
        <div style={{ position: 'absolute', width: '500px', height: '500px', borderRadius: '50%', background: 'rgba(124,58,237,0.1)', filter: 'blur(80px)', top: '-60px', left: '10%', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(79,142,247,0.1)', filter: 'blur(80px)', top: '0', right: '10%', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: '999px', padding: '6px 16px', marginBottom: '24px' }}>
            <span style={{ fontSize: '1rem' }}>⚡</span>
            <span style={{ fontSize: '.75rem', fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '.1em' }}>A Gantima Product</span>
          </div>

          {/* Product name */}
          <div style={{ fontSize: 'clamp(3rem,7vw,6rem)', fontWeight: 900, letterSpacing: '-2px', lineHeight: 1, marginBottom: '12px' }}>
            <span style={{ background: 'linear-gradient(135deg,#4f8ef7 0%,#7c3aed 50%,#06b6d4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>ViralVolt</span>
          </div>

          <h1 style={{ fontSize: 'clamp(1.4rem,2.5vw,2rem)', fontWeight: 700, color: '#94a3b8', marginBottom: '20px', letterSpacing: '-0.5px' }}>
            Go Viral on Autopilot with AI
          </h1>

          <p style={{ fontSize: '1.1rem', color: '#64748b', maxWidth: '600px', margin: '0 auto 40px', lineHeight: 1.8 }}>
            ViralVolt generates scroll-stopping captions, hashtags, and posts for every platform in seconds. Stop guessing what goes viral — let AI do it for you.
          </p>

          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '48px' }}>
            <button className="mkt-btn mkt-btn-primary mkt-btn-xl mkt-btn-glow" style={{ background: 'linear-gradient(135deg,#7c3aed,#4f8ef7)' }}>
              ⚡ Try ViralVolt Free
            </button>
            <button className="mkt-btn mkt-btn-ghost mkt-btn-xl" onClick={() => navigate('/')}>
              Talk to Gantima →
            </button>
          </div>

          {/* Platform pills */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {PLATFORMS.map((p, i) => (
              <span key={i} style={{ fontSize: '.78rem', color: '#64748b', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '999px', padding: '5px 14px', fontWeight: 600 }}>{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ background: '#030d1d', padding: '48px 5%' }}>
        <div className="mkt-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '20px', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            {[
              { num: '10x', label: 'Faster Content Creation' },
              { num: '6', label: 'Platforms Supported' },
              { num: '50+', label: 'Viral Post Templates' },
              { num: '100%', label: 'AI-Powered' },
            ].map((s, i) => (
              <div key={i} style={{ padding: '24px 16px', background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: '14px' }}>
                <div style={{ fontSize: '2rem', fontWeight: 900, background: 'linear-gradient(135deg,#7c3aed,#4f8ef7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '6px' }}>{s.num}</div>
                <div style={{ fontSize: '.78rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mkt-section" style={{ background: '#020817' }}>
        <div className="mkt-container">
          <div className="mkt-section-header">
            <div className="mkt-pill" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)', color: '#a78bfa' }}>FEATURES</div>
            <h2>Everything You Need to Go Viral</h2>
            <p>Six powerful AI tools built into one product — designed to make your content explode on social media.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '20px', maxWidth: '1100px', margin: '0 auto' }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{ padding: '28px', background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.1)', borderRadius: '18px', transition: 'border-color .2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(124,58,237,0.35)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(124,58,237,0.1)'}>
                <div style={{ fontSize: '2rem', marginBottom: '14px' }}>{f.icon}</div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#f1f5f9', marginBottom: '10px' }}>{f.title}</div>
                <p style={{ fontSize: '.88rem', color: '#64748b', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mkt-section" style={{ background: '#030d1d' }}>
        <div className="mkt-container">
          <div className="mkt-section-header">
            <div className="mkt-pill" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)', color: '#a78bfa' }}>HOW IT WORKS</div>
            <h2>From Idea to Viral Post in 30 Seconds</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '20px', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            {[
              { step: '01', title: 'Enter Your Topic', desc: 'Type what you want to post about — a product, idea, story, or trend.' },
              { step: '02', title: 'Choose a Platform', desc: 'Select Instagram, TikTok, Twitter, LinkedIn or all of them at once.' },
              { step: '03', title: 'AI Generates Content', desc: 'ViralVolt writes captions, hooks, hashtags, and CTAs tailored to go viral.' },
              { step: '04', title: 'Post & Go Viral', desc: 'Copy your content, post it, and watch your engagement explode.' },
            ].map((s, i) => (
              <div key={i} style={{ padding: '28px 20px', background: 'rgba(79,142,247,0.04)', border: '1px solid rgba(79,142,247,0.1)', borderRadius: '18px' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 900, background: 'linear-gradient(135deg,#4f8ef7,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '14px' }}>{s.step}</div>
                <div style={{ fontSize: '.95rem', fontWeight: 800, color: '#f1f5f9', marginBottom: '8px' }}>{s.title}</div>
                <p style={{ fontSize: '.83rem', color: '#64748b', lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mkt-section" style={{ background: '#020817' }}>
        <div className="mkt-container">
          <div className="mkt-section-header">
            <div className="mkt-pill" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)', color: '#a78bfa' }}>FAQ</div>
            <h2>Common Questions</h2>
          </div>
          <div style={{ maxWidth: '720px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {FAQS.map((f, i) => (
              <div key={i} style={{ background: 'rgba(79,142,247,0.04)', border: '1px solid rgba(79,142,247,0.1)', borderRadius: '14px', overflow: 'hidden' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: '100%', textAlign: 'left', padding: '18px 20px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '.95rem', fontWeight: 700, color: '#f1f5f9' }}>{f.q}</span>
                  <span style={{ color: '#4f8ef7', fontSize: '1.2rem', flexShrink: 0, transform: openFaq === i ? 'rotate(45deg)' : 'none', transition: '.2s' }}>+</span>
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 20px 18px', color: '#64748b', lineHeight: 1.75, fontSize: '.88rem' }}>{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mkt-cta-banner" style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(79,142,247,0.1))' }}>
        <div className="mkt-cta-orb mkt-cta-orb-1" /><div className="mkt-cta-orb mkt-cta-orb-2" />
        <div className="mkt-cta-inner">
          <div className="mkt-cta-badge">⚡ VIRALVOLT</div>
          <h2>Ready to Go Viral on Autopilot?</h2>
          <p>Join thousands of creators and brands using ViralVolt to generate high-performing social media content every day — in seconds, not hours.</p>
          <div className="mkt-cta-actions">
            <button className="mkt-btn mkt-btn-primary mkt-btn-xl mkt-btn-glow" style={{ background: 'linear-gradient(135deg,#7c3aed,#4f8ef7)' }}>
              ⚡ Try ViralVolt Free
            </button>
            <span className="mkt-cta-note">Free to try · No credit card required</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mkt-footer">
        <div className="mkt-container">
          <div className="mkt-footer-grid">
            <div className="mkt-footer-brand">
              <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px', textDecoration:'none' }}>
                <img src="/GANTIMA-Logo.png" alt="Gantima" style={{ height:'48px', width:'auto', objectFit:'contain', filter:'drop-shadow(0 0 8px rgba(79,142,247,0.4))', flexShrink:0 }} />
                <span style={{ fontSize:'.55rem', fontWeight:800, color:'#4f8ef7', letterSpacing:'2.5px', textTransform:'uppercase', borderLeft:'1px solid rgba(79,142,247,0.25)', paddingLeft:'10px', lineHeight:1.5 }}>SOFTWARE<br/>AUTOMATION</span>
              </a>
              <p>AI automation agency helping businesses eliminate manual work and scale intelligently.</p>
              <a href="mailto:support@gantima.com" className="mkt-footer-email">support@gantima.com</a>
            </div>
            <div className="mkt-footer-col"><h4>Products</h4><ul>
              <li><a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer">ViralVolt.ai</a></li>
            </ul></div>
            <div className="mkt-footer-col"><h4>Services</h4><ul>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>AI Chatbot</a></li>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Workflow Automation</a></li>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Trading Bot</a></li>
            </ul></div>
            <div className="mkt-footer-col"><h4>Company</h4><ul>
              <li><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a></li>
              <li><a href="/about" onClick={e => { e.preventDefault(); navigate('/about'); }}>About</a></li>
              <li><a href="/pricing" onClick={e => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a></li>
              <li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li>
            </ul></div>
          </div>
          <div className="mkt-footer-bottom">
            <span>© 2026 Gantima Software. All rights reserved.</span>
            <a href="/privacy" onClick={e => { e.preventDefault(); navigate('/privacy'); }} style={{ color: '#64748b' }}>Privacy Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
